import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  FormHelperText,
  Typography,
} from '@mui/material';
import { T } from '../i18n';

// Costanti di conversione e limiti
const CM_MIN = 60;
const CM_MAX = 240;
const CM_TO_INCH = 0.393701;

// Definizione della struttura dell'oggetto HeightItem
interface HeightItem {
  name: string;
  index: number;
  tall: number | undefined; // Valore in cm (può essere undefined)
}

// Definizione delle Props del componente
interface MultiHeightSelectorProps {
  values: HeightItem[]; // Lista di oggetti con nome e altezza
  onChange: (index: number, newValue: number | undefined) => void; // Restituisce l'indice e il nuovo valore in cm
}

export const HeightSelector: React.FC<MultiHeightSelectorProps> = ({ values, onChange }) => {
  // Lo stato dell'unità di misura è unico (globale) per tutto il componente
  const [unit, setUnit] = useState<'cm' | 'feet'>('cm');

  // Stati locali per gestire i testi digitati riga per riga
  const [cmInputs, setCmInputs] = useState<string[]>([]);
  const [feetInputs, setFeetInputs] = useState<string[]>([]);
  const [inchInputs, setInchInputs] = useState<string[]>([]);

  // Funzioni matematiche di conversione
  const convertCmToFeetInches = (cm: number) => {
    const totalInches = cm * CM_TO_INCH;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round((totalInches % 12) * 10) / 10;
    return { feet, inches };
  };

  const convertFeetInchesToCm = (feet: number, inches: number): number => {
    const totalInches = feet * 12 + inches;
    return totalInches / CM_TO_INCH;
  };

  // Sincronizza tutti i campi di testo quando i "values" esterni o l'unità cambiano
  useEffect(() => {
    const newCmInputs: string[] = [];
    const newFeetInputs: string[] = [];
    const newInchInputs: string[] = [];

    values.forEach((item) => {
      const val = item.tall;
      if (val === undefined || val === 0) {
        newCmInputs.push('');
        newFeetInputs.push('');
        newInchInputs.push('');
      } else {
        newCmInputs.push(val.toString());
        const { feet, inches } = convertCmToFeetInches(val);
        newFeetInputs.push(feet.toString());
        newInchInputs.push(inches.toString());
      }
    });

    setCmInputs(newCmInputs);
    setFeetInputs(newFeetInputs);
    setInchInputs(newInchInputs);
  }, [values, unit]);

  // Cambio dell'unità di misura
  const handleUnitChange = (event: SelectChangeEvent<'cm' | 'feet'>) => {
    setUnit(event.target.value as 'cm' | 'feet');
  };

  // Gestione dell'input in Centimetri
  const handleCmChange = (index: number, realIndex: number, stringVal: string) => {
    const updatedCmInputs = [...cmInputs];
    updatedCmInputs[index] = stringVal;
    setCmInputs(updatedCmInputs);
    
    if (stringVal === '') {
      onChange(realIndex, undefined);
      return;
    }

    const numVal = parseFloat(stringVal);
    if (!isNaN(numVal)) {
      onChange(realIndex, numVal);
    }
  };

  // Gestione dell'input in Piedi e Pollici
  const handleFeetInchesChange = (index: number, realIndex: number, field: 'feet' | 'inch', stringVal: string) => {
    const updatedFeetInputs = [...feetInputs];
    const updatedInchInputs = [...inchInputs];

    if (field === 'feet') updatedFeetInputs[index] = stringVal;
    if (field === 'inch') updatedInchInputs[index] = stringVal;

    setFeetInputs(updatedFeetInputs);
    setInchInputs(updatedInchInputs);

    const f = updatedFeetInputs[index];
    const i = updatedInchInputs[index];

    // Se entrambi i campi sono vuoti, restituisce undefined
    if (f === '' && i === '') {
      onChange(realIndex, undefined);
      return;
    }

    const numF = parseFloat(f) || 0;
    const numI = parseFloat(i) || 0;
    
    const newCm = convertFeetInchesToCm(numF, numI);
    onChange(realIndex, newCm);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: "100%", mt: 2 }}>
      
      {/* Menu di Selezione Unità */}
      <FormControl fullWidth>
        <InputLabel id="unit-select-label">{T('user_tall_unit')}</InputLabel>
        <Select
          labelId="unit-select-label"
          value={unit}
          label="Unità di Misura"
          onChange={handleUnitChange}
        >
          <MenuItem value="cm">{T('centimeter')} (cm)</MenuItem>
          <MenuItem value="feet">{T('feet')} / {T('inch')} (ft / in)</MenuItem>
        </Select>
      </FormControl>

      {/* Lista Generata degli Input */}
      {values.map((item, index) => {
        const val = item.tall;
        const hasError = !!val && (val < CM_MIN || val > CM_MAX);

        return (
          <Box key={index} sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
              {T('user_tall')} <strong>{index + 1}° {item.name}</strong>
            </Typography>

            {/* Visualizzazione Condizionale degli Input */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {unit === 'cm' ? (
                <TextField
                  label={T('centimeter')}
                  type="number"
                  required
                  value={cmInputs[index] || ''}
                  onChange={(e) => handleCmChange(index, item.index, e.target.value)}
                  error={hasError}
                  slotProps={{
                    htmlInput: {
                      min: CM_MIN,
                      max: CM_MAX,
                      step: "any"
                    }
                  }}
                  fullWidth
                />
              ) : (
                <>
                  <TextField
                    label={T('feet')}
                    type="number"
                    required
                    value={feetInputs[index] || ''}
                    onChange={(e) => handleFeetInchesChange(index, item.index, 'feet', e.target.value)}
                    error={hasError}
                    slotProps={{
                      htmlInput: {
                        step: "any"
                      }
                    }}
                    fullWidth
                  />
                  <TextField
                    label={T('inch')}
                    type="number"
                    value={inchInputs[index] || ''}
                    onChange={(e) => handleFeetInchesChange(index, item.index, 'inch', e.target.value)}
                    error={hasError}
                    slotProps={{
                      htmlInput: {
                        step: "any"
                      }
                    }}
                    fullWidth
                  />
                </>
              )}
            </Box>

            {/* Messaggi di Errore Validazione specifici per riga */}
            {hasError && (
              <FormHelperText error sx={{ fontSize: '0.85rem', mt: 0.5, mx: 0 }}>
                {unit === 'cm' 
                  ? `${T('user_tall_error')} (Min: ${CM_MIN} cm, Max: ${CM_MAX} cm)`
                  : `${T('user_tall_error')} (Min: 1'11.6", Max: 7'10.5")`}
              </FormHelperText>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default HeightSelector;
